import { useState, useEffect, useMemo, useRef } from 'react';
import { X, LogOut, Trash2, FileText, Calendar, AlertCircle, Database, Clock, Repeat, Minus, Plus, RotateCcw, Tag } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useFirestore } from '../hooks/useFirestore';
import { useFutureTransactions } from '../hooks/useFutureTransactions';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { getAllEasterEggs, getEasterEggsWithCompletionStatus, saveEasterEggCompletion } from '../utils/easterEggs';
import { usePopup } from '../contexts/PopupContext';
import DataManager from './DataManager';
import FutureTransactions from './FutureTransactions';
import RecurringTransactions from './RecurringTransactions';
import { VIRTUAL_DEBT_WALLETS } from '../constants/debtWallets';
import { generateId } from '../utils/id';
import { useSettings } from '../contexts/SettingsContext';

const UserProfile = ({
  isOpen,
  onClose,
  easterEggsWithStatus,
  onExportData,
  onExportStats,
  onImportData,
  onResetData,
  onShowImportModal,
  onUpdateSettings,
  userSettings,
  stores = [],
  onAddStore = async () => { },
  existingPersons = [],
  categoriesFallback = { expense: [], income: [], wallet: [] },
  onRecoverCategories,
  onOpenCategories
}) => {
  const { user, logout, deleteAccount } = useAuth();
  const firestore = useFirestore();
  const {
    deleteAllUserData,
    getCompletedEasterEggs,
    setEasterEggCompleted,
    isEasterEggCompleted,
    deleteDocument,
    addDocument,
    updateDocument
  } = firestore;
  const { showError, showAlert, showConfirm, showSuccess } = usePopup();
  const { moveToCurrent, checkAndMoveExpiredTransactions } = useFutureTransactions();
  const { data: expenses } = firestore.useCollectionData('expenses');
  const { data: incomes } = firestore.useCollectionData('incomes');
  const { data: categoriesData } = firestore.useCollectionData('categories', null);
  const { data: walletsData } = firestore.useCollectionData('wallets', 'createdAt');
  const { data: recurringTransactions } = firestore.useCollectionData('recurringTransactions', 'nextOccurrence');
  const walletsForDisplay = useMemo(() => {
    const baseWallets = Array.isArray(walletsData) ? walletsData : [];
    const existingIds = new Set(baseWallets.map(wallet => wallet.id));
    const virtualWallets = VIRTUAL_DEBT_WALLETS
      .filter(wallet => !existingIds.has(wallet.id))
      .map(wallet => ({ ...wallet }));
    return [...baseWallets, ...virtualWallets];
  }, [walletsData]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showEasterEggs, setShowEasterEggs] = useState(false);
  const avatarTapCount = useRef(0);
  const avatarTapTime = useRef(0);
  const avatarTapTimer = useRef(null);
  const [avatarTapProgress, setAvatarTapProgress] = useState(0); // 0-5 per feedback visivo
  const [activeSection, setActiveSection] = useState('profile'); // 'profile' only
  const [showDataManager, setShowDataManager] = useState(false);
  const [showFutureTransactions, setShowFutureTransactions] = useState(false);
  const [showRecurringTransactions, setShowRecurringTransactions] = useState(false);

  const { fontScale, setFontScale } = useSettings();

  // Controlla automaticamente le transazioni future scadute quando si apre il profilo
  useEffect(() => {
    if (isOpen && expenses && incomes) {
      checkAndMoveExpiredTransactions(expenses, incomes)
        .then(({ movedExpenses, movedIncomes }) => {
          if (movedExpenses > 0 || movedIncomes > 0) {
            showAlert(
              'Transazioni future aggiornate',
              `${movedExpenses + movedIncomes} transazioni future sono state automaticamente spostate a oggi.`
            );
          }
        })
        .catch(error => {
          console.error('Errore durante il controllo delle transazioni future:', error);
        });
    }
  }, [isOpen, expenses, incomes, checkAndMoveExpiredTransactions, showAlert]);

  useEffect(() => {
    if (!isOpen) {
      setShowDataManager(false);
      setShowFutureTransactions(false);
      setShowRecurringTransactions(false);
    }
  }, [isOpen]);

  // Prepara le categorie per i componenti figli
  const categories = useMemo(() => {
    const fallbackExpense = Array.isArray(categoriesFallback?.expense) ? categoriesFallback.expense : [];
    const fallbackIncome = Array.isArray(categoriesFallback?.income) ? categoriesFallback.income : [];
    const fallbackWallet = Array.isArray(categoriesFallback?.wallet) ? categoriesFallback.wallet : [];

    const expenseList = Array.isArray(categoriesData?.expense) && categoriesData.expense.length > 0
      ? categoriesData.expense
      : fallbackExpense;
    const incomeList = Array.isArray(categoriesData?.income) && categoriesData.income.length > 0
      ? categoriesData.income
      : fallbackIncome;
    const walletList = Array.isArray(categoriesData?.wallet) && categoriesData.wallet.length > 0
      ? categoriesData.wallet
      : fallbackWallet;

    return {
      expense: expenseList,
      income: incomeList,
      wallet: walletList
    };
  }, [categoriesData, categoriesFallback]);

  if (!isOpen) return null;

  const totalTransactions = (expenses?.length || 0) + (incomes?.length || 0);
  const registrationDate = user?.metadata?.creationTime
    ? format(new Date(user.metadata.creationTime), 'dd MMMM yyyy', { locale: it })
    : 'Data non disponibile';

  // Easter egg: 5 tap rapidi sulle iniziali nel profilo
  const handleAvatarTap = () => {
    const now = Date.now();
    if (now - avatarTapTime.current > 2000) {
      avatarTapCount.current = 1;
    } else {
      avatarTapCount.current += 1;
    }
    avatarTapTime.current = now;
    setAvatarTapProgress(avatarTapCount.current);

    // Reset visivo dopo 2s di inattività
    if (avatarTapTimer.current) clearTimeout(avatarTapTimer.current);
    avatarTapTimer.current = setTimeout(() => {
      avatarTapCount.current = 0;
      setAvatarTapProgress(0);
    }, 2000);

    if (avatarTapCount.current >= 5) {
      avatarTapCount.current = 0;
      setAvatarTapProgress(0);
      if (avatarTapTimer.current) clearTimeout(avatarTapTimer.current);
      setShowEasterEggs(true);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);

      // Prima elimina tutti i dati Firestore
      await deleteAllUserData();

      // Poi elimina l'account utente
      await deleteAccount();

      // Chiudi il modal
      onClose();


    } catch (error) {
      console.error('Errore durante l\'eliminazione account:', error);

      // Se l'errore è dovuto a login recente richiesto, mostra messaggio specifico
      if (error.code === 'auth/requires-recent-login') {
        const confirmed = confirm(
          'Per eliminare l\'account, devi effettuare nuovamente l\'accesso per motivi di sicurezza.\n\n' +
          'Clicca OK per essere reindirizzato alla pagina di login, dove potrai:\n' +
          '1. Effettuare nuovamente l\'accesso\n' +
          '2. Tornare qui e riprovare a eliminare l\'account\n\n' +
          'Vuoi procedere?'
        );

        if (confirmed) {
          await logout();
          onClose();
        }
      } else {
        showError(`Errore durante l'eliminazione account: ${error.message}`, 'Errore');
      }
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
    } catch (error) {
      console.error('Errore durante il logout:', error);
    }
  };

  const handleFutureTransactions = () => {
    setShowFutureTransactions(true);
  };

  const handleRecurringTransactions = () => {
    setShowRecurringTransactions(true);
  };

  const handleCreateRecurringTransaction = async (transactionData) => {
    try {
      const recurringId = generateId('recurring');
      const firstOccurrence = transactionData.firstOccurrence || transactionData.nextOccurrence;

      await addDocument('recurringTransactions', {
        ...transactionData,
        id: recurringId,
        firstOccurrence,
        occurrencesGenerated: 0,
        active: true
      });

      showSuccess('La transazione ripetuta è stata creata con successo.', 'Transazione ripetuta salvata');
    } catch (error) {
      console.error('Errore durante la creazione della transazione ripetuta:', error);
      showError('Errore durante la creazione della transazione ripetuta.', 'Errore');
      throw error;
    }
  };

  const handleUpdateRecurringTransaction = async (id, updates) => {
    try {
      await updateDocument('recurringTransactions', id, {
        ...updates
      });

      showSuccess('La transazione ripetuta è stata aggiornata con successo.', 'Transazione ripetuta aggiornata');
    } catch (error) {
      console.error('Errore durante l\'aggiornamento della transazione ripetuta:', error);
      showError('Errore durante l\'aggiornamento della transazione ripetuta.', 'Errore');
      throw error;
    }
  };

  const handleDataManager = () => {
    setShowDataManager(true);
  };

  const handleEditFutureTransaction = (transaction) => {
    // Chiudi il modal delle transazioni future
    setShowFutureTransactions(false);
    // Chiudi il modal del profilo utente
    onClose();
    // Emetti un evento custom per aprire il form di modifica
    const editEvent = new CustomEvent('editFutureTransaction', {
      detail: { transaction }
    });
    window.dispatchEvent(editEvent);
  };

  const handleDeleteFutureTransaction = async (transaction) => {
    try {
      // Chiedi conferma prima di eliminare
      const confirmed = await new Promise((resolve) => {
        showConfirm(
          <div>
            <p className="mb-3">Sei sicuro di voler eliminare questa transazione futura?</p>
            <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
              <strong>{transaction.store}</strong> - {transaction.amount}€
            </p>
            <p className="font-medium">Questa azione non può essere annullata.</p>
          </div>,
          'Elimina Transazione Futura',
          () => resolve(true),  // Conferma
          () => resolve(false)  // Annulla
        );
      });

      if (!confirmed) {
        return;
      }

      // Elimina la transazione
      const collectionName = transaction._type === 'expense' ? 'expenses' : 'incomes';
      await deleteDocument(collectionName, transaction.id);

      // Mostra messaggio di successo
      showAlert('Transazione eliminata', 'La transazione futura è stata eliminata con successo.');

    } catch (error) {
      console.error('Errore durante l\'eliminazione:', error);
      showError('Errore durante l\'eliminazione della transazione', 'Errore');
    }
  };

  const handleDeleteRecurringTransaction = async (transaction) => {
    try {
      const confirmed = await new Promise((resolve) => {
        showConfirm(
          <div>
            <p className="mb-3">Sei sicuro di voler eliminare questa transazione ripetuta?</p>
            <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
              <strong>{transaction.store || 'Transazione ripetuta'}</strong> - {parseFloat(transaction.amount).toFixed(2)}€
            </p>
            <p className="font-medium">Questa azione non può essere annullata.</p>
          </div>,
          'Elimina transazione ripetuta',
          () => resolve(true),
          () => resolve(false)
        );
      });

      if (!confirmed) {
        return;
      }

      await deleteDocument('recurringTransactions', transaction.id || transaction.documentId);
      showAlert('La transazione ripetuta è stata eliminata.', 'Transazione eliminata');
    } catch (error) {
      console.error('Errore durante l\'eliminazione della transazione ripetuta:', error);
      showError('Errore durante l\'eliminazione della transazione ripetuta.', 'Errore');
      throw error;
    }
  };

  const handleMoveToCurrent = async (transaction) => {
    try {
      await moveToCurrent(transaction);
      showAlert('Transazione spostata', 'La transazione è stata spostata a oggi e ora è visibile nelle transazioni normali.');
    } catch (error) {
      console.error('Errore durante lo spostamento:', error);
      showError('Errore durante lo spostamento della transazione', 'Errore');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-sm w-full animate-fade-in-up" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-blue-600/90 backdrop-blur-sm text-white p-4 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">
              Profilo Utente
            </h2>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Avatar e nome */}
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center cursor-pointer select-none transition-all duration-150 active:scale-90
                ${avatarTapProgress > 0 ? 'ring-2 ring-offset-1 ring-blue-400' : ''}
                ${avatarTapProgress >= 3 ? 'ring-yellow-400' : ''}
                ${avatarTapProgress >= 4 ? 'ring-green-400 animate-pulse' : ''}`}
              onClick={handleAvatarTap}
              title="Tap 5 volte per una sorpresa... 🎉"
            >
              <span className="text-white font-semibold text-base pointer-events-none">
                {(() => {
                  const name = user?.displayName || user?.email;
                  const words = name?.split(' ').filter(word => word.length > 0) || [];
                  if (words.length >= 2) {
                    return (words[0][0] + words[1][0]).toUpperCase();
                  } else if (words.length === 1) {
                    return words[0].substring(0, 2).toUpperCase();
                  } else {
                    return name?.substring(0, 2).toUpperCase() || 'U';
                  }
                })()}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {user?.displayName || 'Utente'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {user?.email}
              </p>
            </div>
          </div>

          {/* Statistiche */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  Transazioni
                </span>
              </div>
              <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                {totalTransactions}
              </p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  Registrato
                </span>
              </div>
              <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                {registrationDate}
              </p>
            </div>
          </div>

          {/* Impostazioni Dimensione Testo */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Dimensione Testo
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded-lg min-w-[3rem] text-center">
                  {Math.round(fontScale * 100)}%
                </span>
                <button
                  onClick={() => setFontScale(1)}
                  className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                  title="Ripristina 100%"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setFontScale(prev => Math.max(prev - 0.05, 0.8))}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
              >
                <Minus className="w-5 h-5" />
              </button>

              <input
                type="range"
                min="0.8"
                max="1.3"
                step="0.05"
                value={fontScale}
                onChange={(e) => setFontScale(parseFloat(e.target.value))}
                className="flex-1 h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />

              <button
                onClick={() => setFontScale(prev => Math.min(prev + 0.05, 1.3))}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>


          {/* Azioni */}
          <div className="space-y-2">
            <button
              onClick={handleDataManager}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors duration-200"
            >
              <Database className="w-4 h-4" />
              <span>Gestione Dati</span>
            </button>

            <button
              onClick={() => {
                onOpenCategories();
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors duration-200"
            >
              <Tag className="w-4 h-4" />
              <span>Gestione Categorie</span>
            </button>

            <button
              onClick={handleFutureTransactions}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors duration-200"
            >
              <Clock className="w-4 h-4" />
              <span>Transazioni Future</span>
            </button>

            <button
              onClick={handleRecurringTransactions}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors duration-200"
            >
              <Repeat className="w-4 h-4" />
              <span>Transazioni Ripetute</span>
            </button>

            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors duration-200"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>

            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-xl transition-colors duration-200"
            >
              <Trash2 className="w-4 h-4" />
              <span>Elimina Account</span>
            </button>
          </div>
        </div>
      </div>

      {/* Conferma eliminazione aggressiva */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => {
          setShowDeleteConfirm(false);
          setDeleteConfirmation('');
        }}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full animate-fade-in-up" onClick={e => e.stopPropagation()}>
            {/* Header rosso */}
            <div className="bg-red-600/90 backdrop-blur-sm text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmation('');
                  }}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Trash2 className="w-5 h-5" />
                  Elimina Account
                </h2>
                <div className="w-6"></div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="text-center">
                <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  ATTENZIONE!
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Stai per eliminare <strong>DEFINITIVAMENTE</strong> il tuo account:
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 mb-6">
                  <li>• Tutti i tuoi dati personali</li>
                  <li>• Tutte le spese e entrate</li>
                  <li>• Tutte le categorie personalizzate</li>
                  <li>• Tutti i negozi salvati</li>
                  <li>• Tutti i conti e saldi</li>
                  <li>• Il tuo account di accesso</li>
                  <li>• Tutte le impostazioni</li>
                </ul>
                <p className="text-red-600 font-semibold">
                  Questa azione è <strong>IRREVERSIBILE</strong> e non può essere annullata!
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Per confermare, scrivi <strong>ELIMINA</strong>:
                </label>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="Scrivi ELIMINA per continuare"
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmation('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200"
                >
                  Annulla
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (deleteConfirmation === 'ELIMINA') {
                      handleDeleteAccount();
                    }
                  }}
                  disabled={deleteConfirmation !== 'ELIMINA' || isDeleting}
                  className="flex-1 px-4 py-2 bg-red-600/90 backdrop-blur-sm text-white rounded-xl font-medium hover:bg-red-700/90 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Elimina Account
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Easter Egg Modal (Medagliere) */}
      {showEasterEggs && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowEasterEggs(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-sm w-full animate-fade-in-up" onClick={e => e.stopPropagation()}>
            {/* Header con X di chiusura */}
            <div className="bg-blue-600/90 backdrop-blur-sm text-white p-4 rounded-t-3xl flex items-center justify-between">
              <h2 className="text-xl font-bold">Easter Egg</h2>
              <button
                onClick={() => setShowEasterEggs(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="text-center mb-6">
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Colleziona tutte le medaglie nascoste!
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {easterEggsWithStatus.filter(egg => egg.isCompleted).length} / {easterEggsWithStatus.length} completate
                </p>
              </div>

              {/* Griglia distintivi stile medaglie Pokémon */}
              <div className="grid grid-cols-3 gap-3 max-h-64 overflow-y-auto">
                {easterEggsWithStatus.map((egg, index) => {
                  return (
                    <button
                      key={egg.id}
                      onClick={() => {
                        if (egg.isCompleted) {
                          // Se completato, mostra titolo e descrizione
                          showAlert(`${egg.description}`, egg.title);
                        } else {
                          // Se non completato, mostra solo il titolo
                          showAlert('Questo easter egg è ancora bloccato. Continua a esplorare l\'app per sbloccarlo!', egg.title);
                        }
                      }}
                      className="group relative"
                    >
                      <div className={`
                        w-14 h-14 mx-auto rounded-full 
                        ${egg.isCompleted
                          ? `bg-gradient-to-br ${egg.bgColor} shadow-lg`
                          : 'bg-gray-300 dark:bg-gray-600 shadow-md'
                        }
                        border-4 border-white dark:border-gray-700
                        flex items-center justify-center text-xl
                        transform transition-all duration-300
                        hover:scale-110 hover:shadow-xl
                        active:scale-95
                        group-hover:animate-pulse
                      `}>
                        {egg.isCompleted ? egg.icon : '❓'}
                      </div>
                      <div className="text-center mt-1">
                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                          {egg.isCompleted ? 'Completato' : 'Bloccato'}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="text-center pt-6">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Tocca una medaglia per scoprire il suo segreto! 🔍
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Gestione Dati */}
      {showDataManager && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDataManager(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-md w-full animate-fade-in-up" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-blue-600/90 backdrop-blur-sm text-white p-4 rounded-t-3xl flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Database className="w-5 h-5" />
                Gestione Dati
              </h2>
              <button
                onClick={() => setShowDataManager(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4">
              <DataManager
                onExportData={onExportData}
                onExportStats={onExportStats}
                onImportData={onImportData}
                onResetData={onResetData}
                onShowImportModal={onShowImportModal}
                userSettings={userSettings}
                onUpdateSettings={onUpdateSettings}
                onRecoverCategories={onRecoverCategories}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Transazioni Future */}
      {showFutureTransactions && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowFutureTransactions(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-md w-full animate-fade-in-up" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-blue-600/90 backdrop-blur-sm text-white p-4 rounded-t-3xl flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Transazioni Future
              </h2>
              <button
                onClick={() => setShowFutureTransactions(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4">
              <FutureTransactions
                expenses={expenses || []}
                incomes={incomes || []}
                wallets={walletsForDisplay}
                categories={categories}
                onEdit={handleEditFutureTransaction}
                onDelete={handleDeleteFutureTransaction}
                onMoveToCurrent={handleMoveToCurrent}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Transazioni Ripetute */}
      {showRecurringTransactions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm z-10"
            onClick={() => setShowRecurringTransactions(false)}
          />
          <div
            className="relative z-20 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-purple-600/90 backdrop-blur-sm text-white p-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setShowRecurringTransactions(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Repeat className="w-5 h-5" />
                  Transazioni Ripetute
                </h2>
                <div className="w-6"></div>
              </div>
            </div>

            <div className="flex-1 overflow-hidden min-h-0">
              <RecurringTransactions
                recurringTransactions={recurringTransactions || []}
                categories={categories}
                wallets={walletsForDisplay}
                stores={stores}
                existingPersons={existingPersons}
                onAddStore={onAddStore}
                onCreate={handleCreateRecurringTransaction}
                onUpdate={handleUpdateRecurringTransaction}
                onDelete={handleDeleteRecurringTransaction}
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default UserProfile; 
